import * as THREE from 'three';
import { TILE_SIZE } from '../../labyrinth/Labyrinth';
import { applyStylizedShading, type LightmapBindings } from '../StylizedShading';

// Exported so the rig can place wheels along the chassis length and seat the
// turret on top. Keep these in sync if you swap to a Blender chassis model.
export const CHASSIS_W = TILE_SIZE * 0.56;   // X (lateral)
export const CHASSIS_H = TILE_SIZE * 0.22;
export const CHASSIS_L = TILE_SIZE * 0.76;   // Z (forward/back — +X is forward in local)
export const CHASSIS_Y = TILE_SIZE * 0.18;   // chassis center height above ground

// Tint pre-attenuated (~42% of original) for the brightness pass: lightmap
// ambient ~0.82 + ACES + 1.35 exposure used to wash the body out.
const COLOR = 0x4d5056;

const SHADING = {
  lightmapNormalOffset: false,
  keyLightStrength: 0.30,
  rimStrength: 0.55,
  rimPower: 2.5,
  verticalAOStrength: 0.22,
} as const;

const SKIN = {
  panelScale: 0.35, etchStrength: 0.38, ledDensity: 0.10,
  conduitStrength: 0.18, trimHue: 0.55, // cyan-teal
} as const;

export interface RobotChassisPart {
  /** Mesh ready to add to the chassis-yaw group. Pre-positioned at y = CHASSIS_Y. */
  readonly object: THREE.Object3D;
  dispose(): void;
}

/**
 * Procedural chassis box. To replace with a Blender import: re-implement this
 * factory to load a glTF and return its root group. Keep `object.position.y =
 * CHASSIS_Y` (or bake the offset into the model) and keep the exported
 * dimensions in line with the new mesh so wheel/turret attachment lines up.
 */
export function createRobotChassis(lightmap: LightmapBindings): RobotChassisPart {
  const geo = new THREE.BoxGeometry(CHASSIS_L, CHASSIS_H, CHASSIS_W);
  const mat = new THREE.MeshBasicMaterial({ color: COLOR });
  applyStylizedShading(mat, { lightmap, ...SHADING, skin: { seed: 0, ...SKIN } });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.y = CHASSIS_Y;
  return {
    object: mesh,
    dispose: () => { geo.dispose(); mat.dispose(); },
  };
}
